import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { 
  QuoteDetails, 
  RfqSource,
  QuoteAssistV2Data
} from '../../quote-assist-v2.model';
import {
  QuoteDetailsChangeEvent,
  ComponentValidationState,
  ComponentConfig,
  FieldChangeEvent,
  FieldValidationEvent
} from '../../models/component-events.model';
import { QuoteAssistV2Service, SaveQuoteDetailsResponse } from '../../../../services/quote-assist-v2.service';
import { LoadingService } from '../../../../services/loading.service';
import { Observable } from 'rxjs';
import { PhoneFormatDirective } from '../../../shared/directives/phone-format.directive';
import { ToastrService } from 'ngx-toastr';
import { QuoteAssistv2BaseComponent } from '../../main/quote-assist-v2-base.component';
import { MixPanelEvents } from '../../../shared/enums';
import { ActivatedRoute } from '@angular/router';
@Component({
  selector: 'app-quote-details-form',
  standalone: true,
  imports: [CommonModule, FormsModule, PhoneFormatDirective],
  templateUrl: './quote-details-form.component.html',
  styleUrls: ['./quote-details-form.component.scss','../../main/quote-assist-v2.disabled-overlay.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuoteDetailsFormComponent extends QuoteAssistv2BaseComponent implements OnInit, OnDestroy, OnChanges {
  @Input() quoteDetails!: QuoteDetails;
  @Input() sourceType!: RfqSource;
  @Input() config!: ComponentConfig;
  @Input() validationState!: ComponentValidationState;
  @Input() integrationType: 'MFG' | 'MFGDirect' | 'SUW' = 'MFG';
  @Input() contactId?: number; // Added for future functionality like PDF downloads
  @Input() currentRfqName?: string; // NEW: Input to receive current RFQ name from parent
  @Output() dataChange = new EventEmitter<QuoteDetailsChangeEvent>();
  @Output() stepNavigation = new EventEmitter<any>();
  @Output() saveSuccess = new EventEmitter<SaveQuoteDetailsResponse>();
  @Output() saveError = new EventEmitter<{ error: string; fieldErrors: { [fieldName: string]: string } }>();
  @Output() triggerRfqNameValidation = new EventEmitter<void>(); // NEW: Event to trigger RFQ name validation
  @Input() isViewMode: boolean = false;

  // Component state
  localQuoteDetails: QuoteDetails = this.initializeQuoteDetails();
  private validationErrors: { [fieldName: string]: string } = {};
  private fieldTouched: { [fieldName: string]: boolean } = {};
  private fieldDirty: { [fieldName: string]: boolean } = {}; // Track if field has been modified
  private subscriptions: Subscription[] = [];
  private isSubmitting: boolean = false;
  private validationDeferred: boolean = true; // NEW: Control when validation should be shown
  queryParams = {
    companyId: 0,
    rfqPartId: 0,
    contactId: 0,
    isTrackingEnabled: 'false',
    integration: '',
  };

  constructor(
    private quoteAssistV2Service: QuoteAssistV2Service,
    private loadingService: LoadingService,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService
  ) { super();}

  private readonly validationRules: { [key: string]: any } = {
    rfqId: { required: false },
    rfqName: { required: true, minLength: 2, maxLength: 100 },
    companyName: { required: true, minLength: 2, maxLength: 100 },
    companyEmail: { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
    companyPhone: { required: true, pattern: /^[\+]?[1-9][\d]{9}$/ },
    address: { required: true, minLength: 5, maxLength: 200 },
    city: { required: true, minLength: 2, maxLength: 50 },
    state: { required: true, minLength: 2, maxLength: 50},
    zipCode: { required: true, pattern: /^[A-Za-z0-9-]{2,4}\s?[A-Za-z0-9-]{2,4}$|^[0-9]{6}$|^[0-9]{5}-[0-9]{3,4}$/},
    quoteNumber: { required: true, minLength: 3, maxLength: 50 },
    quoteExpiryDate: { required: true, minDate: this.getMinValidUntilDate() },
    paymentTerm: { required: true }
  };

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['companyId']) {
        this.queryParams.companyId = Number(params['companyId']);
        this.queryParams.rfqPartId = Number(params['rfqPartId']);
        this.queryParams.contactId = Number(params['contactId']);
        this.queryParams.isTrackingEnabled = params['isTrackingEnabled'];
        this.queryParams.integration = params['integration'];
        // this.isTrackingEnabled = this.queryParams.isTrackingEnabled ;
      }
    });
    if (!this.quoteDetails || !this.quoteDetails.rfqId) {
      this.initializeComponent();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteDetails'] && changes['quoteDetails'].currentValue) {
      const currentValue = changes['quoteDetails'].currentValue;
      const previousValue = changes['quoteDetails'].previousValue;
      
      const hasSignificantChanges = this.hasSignificantDataChanges(currentValue, previousValue);
      
      if (hasSignificantChanges) {
        this.localQuoteDetails = { ...currentValue };
        this.emitDataChange();
      } else if (previousValue && currentValue.rfqName !== previousValue.rfqName) {
        this.localQuoteDetails.rfqName = currentValue.rfqName;
        if (!this.validationDeferred) {
          this.validateField('rfqName', currentValue.rfqName);
        }
        this.emitDataChange();
      } else if (!previousValue) {
        this.localQuoteDetails = { ...currentValue };
        this.emitDataChange();
      }
    }

    // NEW: Handle changes to currentRfqName input
    if (changes['currentRfqName'] && changes['currentRfqName'].currentValue !== undefined) {
      const newRfqName = changes['currentRfqName'].currentValue;
      if (newRfqName !== this.localQuoteDetails.rfqName) {
        this.localQuoteDetails.rfqName = newRfqName;
        // Only validate if validation is not deferred
        if (!this.validationDeferred) {
          this.validateField('rfqName', newRfqName);
        }
        this.emitDataChange();
      }
    }
  }

  /**
   * Check if there are significant changes that warrant updating all fields
   * This helps distinguish between user edits and data loading from IndexedDB/API
   */
  private hasSignificantDataChanges(currentValue: QuoteDetails, previousValue: QuoteDetails | undefined): boolean {
    if (!previousValue) {
      return true; // Initial load
    }
    
    // Check if multiple fields have changed (indicating data load from IndexedDB/API)
    const fieldsToCheck = ['companyName', 'companyEmail', 'companyPhone', 'address', 'city', 'state', 'zipCode', 'quoteNumber', 'quoteExpiryDate', 'paymentTerm'];
    const changedFields = fieldsToCheck.filter(field => 
      currentValue[field as keyof QuoteDetails] !== previousValue[field as keyof QuoteDetails]
    );
    
    // If more than 2 fields changed, it's likely a data load from IndexedDB/API
    const result = changedFields.length > 2;
    
    return result;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // ===== INITIALIZATION =====

  private initializeComponent(): void {
    // Only initialize localQuoteDetails if quoteDetails has meaningful data
    if (this.quoteDetails && this.quoteDetails.rfqId) {
      this.localQuoteDetails = { ...this.quoteDetails };
    } else {
      this.localQuoteDetails = this.initializeQuoteDetails();
    }
    this.configureValidationRules();
  }

  private configureValidationRules(): void {
    // All shipping address fields are required for all integration types
    // to maintain consistent validation across the form
    this.validationRules['address'].required = true;
    this.validationRules['city'].required = true;
    this.validationRules['state'].required = true;
    this.validationRules['zipCode'].required = true;
  }

  // ===== FIELD CHANGE HANDLERS =====

  onFieldChange(fieldName: keyof QuoteDetails, value: any): void {
    (this.localQuoteDetails as any)[fieldName] = value;
    this.fieldTouched[fieldName] = true;
    this.fieldDirty[fieldName] = true; // Mark field as dirty when user changes it
    
    // REMOVED: Immediate validation - only track changes
    // Validation will happen on form submission or when explicitly triggered
    
    const fieldEvent: FieldChangeEvent = {
      fieldName,
      value,
      isValid: true, // Assume valid until validation is triggered
      error: undefined,
      warning: this.getFieldWarning(fieldName, value),
      touched: true
    };

    this.updateValidationState(fieldEvent);
    this.emitDataChange();
  }

  onFieldBlur(fieldName: keyof QuoteDetails): void {
    this.fieldTouched[fieldName] = true;
    // REMOVED: Immediate validation on blur
    // Validation will happen on form submission or when explicitly triggered
    this.emitDataChange();
  }

  onFieldFocus(fieldName: keyof QuoteDetails): void {
    // Only clear error when user starts editing if validation is not deferred
    if (!this.validationDeferred && this.validationErrors[fieldName]) {
      delete this.validationErrors[fieldName];
      this.emitDataChange();
    }
    // Don't trigger validation on focus - only track that field was touched
  }

  onQuoteExpiryDateChange(value: string): void {
    this.onFieldChange('quoteExpiryDate', value?.trim() || '');
  }

  onQuoteExpiryDateBlur(): void {
    const rawValue = this.localQuoteDetails.quoteExpiryDate || '';
    const parsedDate = this.parseAndValidateDate(rawValue);
    if (rawValue && !parsedDate) {
      // Keep manual entry enabled, but do not retain invalid date values.
      this.localQuoteDetails.quoteExpiryDate = '';
    } else {
      this.localQuoteDetails.quoteExpiryDate = parsedDate ? this.formatDateAsIso(parsedDate) : '';
    }

    this.onFieldBlur('quoteExpiryDate');
  }

  // ===== VALIDATION =====

  private validateField(fieldName: keyof QuoteDetails, value: any): boolean {
    const rules = this.validationRules[fieldName];
    if (!rules) {
      return true;
    }

    let isValid = true;
    let errorMessage = '';

    // Required validation - only show error if validation is not deferred
    if (rules.required && (!value || value.toString().trim().length === 0)) {
      // Only show required error if validation is not deferred
      if (!this.validationDeferred) {
        isValid = false;
        errorMessage = `${this.getFieldDisplayName(fieldName)} is required`;
      }
    }

    // Min length validation - only if field has value and validation is not deferred
    if (isValid && rules.minLength && value && value.toString().trim().length < rules.minLength && !this.validationDeferred) {
      isValid = false;
      errorMessage = `${this.getFieldDisplayName(fieldName)} must be at least ${rules.minLength} characters`;
    }

    // Max length validation - only if field has value and validation is not deferred
    if (isValid && rules.maxLength && value && value.toString().trim().length > rules.maxLength && !this.validationDeferred) {
      isValid = false;
      errorMessage = `${this.getFieldDisplayName(fieldName)} must be no more than ${rules.maxLength} characters`;
    }

    // Pattern validation - only if field has value and validation is not deferred
    if (isValid && rules.pattern && value && !this.validationDeferred) {
      const stringValue = value.toString().trim();
      if (fieldName === 'companyPhone') {
        // Remove non-digits for phone validation
        // const cleanPhone = stringValue.replace(/\D/g, '');
        // if (!rules.pattern.test(cleanPhone)) {
        //   isValid = false;
        //   errorMessage = `Please enter a valid phone number (10 digits)`;
        // }
      } else if (!rules.pattern.test(stringValue)) {
        isValid = false;
        errorMessage = `Please enter a valid ${this.getFieldDisplayName(fieldName).toLowerCase()}`;
      }
    }

    // Date validation - only if field has value and validation is not deferred
    if (isValid && fieldName === 'quoteExpiryDate' && value && !this.validationDeferred) {
      const selectedDate = this.parseAndValidateDate(value);
      if (!selectedDate) {
        isValid = false;
        errorMessage = 'Please enter a valid date (month/day/year)';
      } else {
        const minDate = new Date(rules.minDate);
        if (selectedDate <= minDate) {
          isValid = false;
          errorMessage = 'Quote expiry date must be at least tomorrow';
        }
      }
    }

    // Update validation state
    if (isValid) {
      delete this.validationErrors[fieldName];
    } else {
      this.validationErrors[fieldName] = errorMessage;
    }

    return isValid;
  }

  private validateAllFields(): void {
    // Enable validation display when validating all fields (e.g., on form submission)
    this.validationDeferred = false;
    
    Object.keys(this.localQuoteDetails).forEach(fieldName => {
      // Skip rfqId validation as it's not mandatory for new RFQ entries
      if (fieldName === 'rfqId') {
        return;
      }
      const fieldKey = fieldName as keyof QuoteDetails;
      // Mark all fields as touched when validating all fields (e.g., on form submission)
      this.fieldTouched[fieldKey] = true;
      this.validateField(fieldKey, this.localQuoteDetails[fieldKey]);
    });
  }

  private getFieldError(fieldName: keyof QuoteDetails, value: any): string | undefined {
    return this.validationErrors[fieldName];
  }

  private getFieldWarning(fieldName: keyof QuoteDetails, value: any): string | undefined {
    // Add warnings for specific fields
    if (fieldName === 'companyEmail' && value) {
      const email = value.toString().toLowerCase();
      if (email.includes('test') || email.includes('example')) {
        return 'Please use a valid business email address';
      }
    }
    return undefined;
  }

  // ===== UTILITY METHODS =====

  private getFieldDisplayName(fieldName: keyof QuoteDetails): string {
    const displayNames: { [key: string]: string } = {
      companyName: 'Company Name',
      companyEmail: 'Company Email',
      companyPhone: 'Company Phone',
      address: 'Address',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      quoteNumber: 'Quote Number',
      quoteExpiryDate: 'Quote Expiry Date',
      paymentTerm: 'Payment Terms'
    };
    return displayNames[fieldName] || fieldName;
  }

  getMinValidUntilDate(): string {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }

  getMaxValidUntilDate(): string {
    return '9999-12-31';
  }

  private parseAndValidateDate(value: unknown): Date | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    // Native date input typically emits YYYY-MM-DD.
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);
    if (isoMatch) {
      const year = Number(isoMatch[1]);
      const month = Number(isoMatch[2]);
      const day = Number(isoMatch[3]);
      return this.buildValidDate(year, month, day);
    }

    // Fallback support for manually typed MM/DD/YYYY and DD/MM/YYYY.
    const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmedValue);
    if (!slashMatch) {
      return null;
    }

    const firstPart = Number(slashMatch[1]);
    const secondPart = Number(slashMatch[2]);
    const year = Number(slashMatch[3]);
    const { month, day } = this.resolveSlashDateParts(firstPart, secondPart);
    return this.buildValidDate(year, month, day);
  }

  private resolveSlashDateParts(firstPart: number, secondPart: number): { month: number; day: number } {
    // Unambiguous DD/MM/YYYY
    if (firstPart > 12 && secondPart <= 12) {
      return { month: secondPart, day: firstPart };
    }

    // Unambiguous MM/DD/YYYY
    if (secondPart > 12 && firstPart <= 12) {
      return { month: firstPart, day: secondPart };
    }

    // Ambiguous values (both <= 12): keep existing behavior as month/day.
    return { month: firstPart, day: secondPart };
  }

  private formatDateAsIso(date: Date): string {
    const year = date.getFullYear().toString().padStart(4, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildValidDate(year: number, month: number, day: number): Date | null {
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000 || year > 9999) {
      return null;
    }

    const candidate = new Date(year, month - 1, day);
    const isExactMatch =
      candidate.getFullYear() === year &&
      candidate.getMonth() === month - 1 &&
      candidate.getDate() === day;

    return isExactMatch ? candidate : null;
  }

  private updateValidationState(fieldEvent: FieldChangeEvent): void {
    const validationEvent: FieldValidationEvent = {
      fieldName: fieldEvent.fieldName,
      isValid: fieldEvent.isValid,
      error: fieldEvent.error,
      warning: fieldEvent.warning,
      validationRules: [this.validationRules[fieldEvent.fieldName as keyof QuoteDetails]]
    };
  }

  private emitDataChange(): void {
    const isValid = this.isFormValid();
    const stepCompleted = this.isStepCompleted();

    const event: QuoteDetailsChangeEvent = {
      data: { ...this.localQuoteDetails },
      isValid,
      errors: { ...this.validationErrors },
      stepCompleted
    };

    this.dataChange.emit(event);
  }

  private isFormValid(): boolean {
    return Object.keys(this.validationErrors).length === 0;
  }

  private isStepCompleted(): boolean {
    // Check if all required fields are filled and valid
    const requiredFields = Object.keys(this.validationRules).filter(
      field => this.validationRules[field as keyof QuoteDetails].required
    );

    return requiredFields.every(field => {
      const fieldName = field as keyof QuoteDetails;
      const hasValue = this.localQuoteDetails[fieldName] && 
                      this.localQuoteDetails[fieldName].toString().trim().length > 0;
      
      // For step completion, we only care if the field has a value
      // Validation errors will be shown when user tries to proceed or when field is touched
      return hasValue;
    });
  }

  /**
   * Save draft data to both IndexedDB and backend
   * This method can be called from multiple places and handles both local and remote saving
   * @param quoteData Optional QuoteAssistV2Data to save, uses current data if not provided
   * @param showLoading Whether to show loading indicator (default: true)
   * @returns Observable with the saved rfqId
   */
  saveDraft(quoteData?: QuoteAssistV2Data, showLoading: boolean = true): Observable<string> {
    // Update quote date to current date when saving draft
    this.localQuoteDetails.quoteDate = new Date().toISOString().split('T')[0];
    
    const dataToSave = quoteData || {
      rfqId: this.localQuoteDetails.rfqId || '',
      sourceType: this.integrationType,
      lastUpdated: new Date().toISOString(),
      quoteDetails: this.localQuoteDetails,
      partDetails: [],
      costBreakdown: {
        materials: { totalCost: 0, items: [] },
        tooling: { totalCost: 0, items: [] },
        manufacturing: { totalCost: 0, items: [] },
        labor: { totalCost: 0, items: [] },
        overhead: { totalCost: 0, items: [] },
        otherCosts: { totalCost: 0, items: [] },
        profit: { profitMargin: 0, totalProfit: 0 }
      }
    };

    if (showLoading) {
      this.loadingService.showActionLoading('draft save', 'Saving draft...', 'full-page');
    }

    return new Observable<string>(observer => {
      // First save to IndexedDB
      this.quoteAssistV2Service.saveQuoteAssistV2Progress(dataToSave).subscribe({
        next: (rfqId: string) => {
          // Then save to backend as draft
          this.quoteAssistV2Service.draftDirectRFQQuoteData(
            dataToSave,
            parseInt(dataToSave.quoteDetails.rfqId) || 0,
            parseInt(dataToSave.quoteDetails.rfqId) || 0,
            dataToSave.sourceType
          ).subscribe({
            next: (response) => {
              if (showLoading) {
                this.loadingService.hideLoading();
              }
              
              // Show success notification
              this.toastrService.success('Draft saved successfully');
              
              // Close iFrame popup after successful draft save
              if (window.parent) {
                setTimeout(() => {
                  window.parent.postMessage({
                    closeIframePopup: true
                  }, '*');
                }, 700);
              }
              
              observer.next(rfqId);
              observer.complete();
            },
            error: (error: any) => {
              if (showLoading) {
                this.loadingService.hideLoading();
              }
              
              // Show error notification
              this.toastrService.error('Failed to save draft');
              
              console.error('Error saving draft to backend:', error);
              // Still return the IndexedDB rfqId even if backend save failed
              observer.next(rfqId);
              observer.complete();
            }
          });
        },
        error: (error: any) => {
          if (showLoading) {
            this.loadingService.hideLoading();
          }
          
          // Show error notification
          this.toastrService.error('Failed to save draft');
          
          console.error('Error saving draft to IndexedDB:', error);
          observer.error(error);
        }
      });
    });
  }


  private getMixPanelEventForIntegration(integrationType: 'MFG' | 'MFGDirect' | 'SUW'): string {
    switch (integrationType) {
      case 'MFG':
        return MixPanelEvents.QuoteDetailsCompletedMarketPlace;
      case 'MFGDirect':
        return MixPanelEvents.QuoteDetailsCompletedSelfCreated;
      case 'SUW':
        return MixPanelEvents.QuoteDetailsCompletedWebsiteSubmitted;
      default:
        return MixPanelEvents.QuoteDetailsCompletedSelfCreated;
    }
  }

  proceedToPartQuoting(): void {
    if (this.isSubmitting) {
      return;
    }

    // Enable validation display and validate all fields
    this.validateAllFields();
    
    if (!this.isFormValid()) {
      // Validation failed - stay on current step and show errors
      // The validationDeferred flag is now false, so errors will be visible
      
      // NEW: Trigger RFQ name validation to show error styling
      this.triggerRfqNameValidation.emit();
      
      return;
    }

    this.isSubmitting = true;
    this.loadingService.showActionLoading('quote details', 'Saving Quote Details and proceeding to parts...', 'full-page');

    // Set the quote date to current date in YYYY-MM-DD format
    this.localQuoteDetails.quoteDate = new Date().toISOString().split('T')[0];
    
    // Mark quote details as complete
    this.localQuoteDetails.isQuoteDetailsCompleted = true;
    
    // Get the appropriate MixPanel event for the integration type
    const mixPanelEvent = this.getMixPanelEventForIntegration(this.integrationType);
    this.addTracking(mixPanelEvent, this.queryParams);

    // Emit step navigation event with backend operation context
    this.stepNavigation.emit({
      fromStep: 'details',
      toStep: 'parts-information',
      canProceed: true,
      requiresBackendCall: true,
      skipToConfiguration: this.integrationType === 'MFG', // Add flag for MFG integration
      backendData: {
        quoteDetails: this.localQuoteDetails,
        companyId: this.quoteDetails.companyId || 0,
        sourceType: this.integrationType
      }
    });
  }

  getFormData(): QuoteDetails {
    return { ...this.localQuoteDetails };
  }

  getFormDataForSubmission(): FormData {
          return this.quoteAssistV2Service.createFormDataFromQuoteDetails(
        this.localQuoteDetails,
               this.quoteDetails.companyId || 0,
        this.integrationType,''
      );
  }

  /**
   * Validate form data and return validation result
   * @returns Validation result with errors if any
   */
  validateFormData(): { isValid: boolean; errors: string[]; fieldErrors: { [fieldName: string]: string } } {
    return this.quoteAssistV2Service.validateQuoteDetails(this.localQuoteDetails);
  }

  /**
   * Check if form is currently submitting
   * @returns boolean indicating if form is submitting
   */
  isFormSubmitting(): boolean {
    return this.isSubmitting;
  }

  /**
   * Public method to save draft data
   * Can be called from parent components or other parts of the application
   * @param quoteData Optional QuoteAssistV2Data to save
   * @param showLoading Whether to show loading indicator
   * @returns Observable with the saved rfqId
   */
  saveDraftData(quoteData?: QuoteAssistV2Data, showLoading: boolean = true): Observable<string> {
    return this.saveDraft(quoteData, showLoading);
  }

  /**
   * Handle completion of backend operations
   * Called by parent component after successful backend operation
   */
  onBackendOperationComplete(success: boolean, response?: any): void {
    this.isSubmitting = false;
    this.loadingService.hideLoading();
    
    if (success && response?.data?.rfqId) {
      this.localQuoteDetails.rfqId = response.data.rfqId;
    }
  }

  private initializeQuoteDetails(): QuoteDetails {
    return {
      rfqId: '',
      rfqName: '',
      companyName: '',
      companyEmail: '',
      companyPhone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      quoteNumber: '',
      quoteExpiryDate: '',
      paymentTerm: '',
      quoteDate: '',
      companyId: undefined,
      contactId: undefined,
      isQuoteDetailsCompleted: false
    };
  }

  // ===== PUBLIC METHODS =====

  validate(): boolean {
    this.validateAllFields();
    return this.isFormValid();
  }

  getFieldClass(fieldName: keyof QuoteDetails): string {
    // Only show error styling if validation has been explicitly triggered (validationDeferred is false)
    const hasError = this.validationErrors[fieldName] && this.fieldTouched[fieldName] && !this.validationDeferred;
    const hasValue = this.localQuoteDetails[fieldName] && 
                    this.localQuoteDetails[fieldName].toString().trim().length > 0;
    
    let classes = 'form-control';
    
    if (hasError) {
      classes += ' errorClass';
    } else if (hasValue && this.fieldTouched[fieldName]) {
      classes += ' valid-border';
    }
    
    if (this.config.readonly) {
      classes += ' readonly';
    }
    
    return classes;
  }

  isFieldRequired(fieldName: keyof QuoteDetails): boolean {
    return this.validationRules[fieldName]?.required || false;
  }

  getPaymentTerms(): string[] {
    return [
      'Net 7',
      'Net 15',
      'Net 30',
      'Net 45',
      'Net 60',
      'Cash on Delivery (COD)',
      'Cash with Order (CWO)',
      'Cash in Advance (CIA)',
      'Cash before Shipment (CBS)',
      'Cash Next Delivery (CND)',
      'Upon Receipt',
      'End of Month (EOM)',
      'Net Monthly Account',
      '30 Days, End of Month',
      '60 Days, End of Month',
      '2/10 Net 30',
      '21 MFI (Month Following Invoice)',
      '1MD',
      '2MD'
    ];
  }

  /**
   * Manually trigger validation display
   * Useful for testing or when you want to show validation errors without form submission
   */
  showValidationErrors(): void {
    this.validationDeferred = false;
    this.validateAllFields();
  }

  /**
   * Public method to trigger validation for all fields
   * This will show validation errors immediately
   */
  triggerValidation(): void {
    this.validationDeferred = false;
    this.validateAllFields();
    this.emitDataChange();
  }

  /**
   * Reset validation state and clear all errors
   * Useful for clearing validation after successful submission or when starting fresh
   */
  resetValidationState(): void {
    this.validationErrors = {};
    this.validationDeferred = true;
    this.emitDataChange();
  }

  // ===== TRACKBY FUNCTIONS =====

  trackByFieldName(index: number, fieldName: string): string {
    return fieldName;
  }

  // Helper methods for template validation state access
  hasFieldError(fieldName: string): boolean {
    // Only show error if validation has been explicitly triggered (validationDeferred is false), 
    // field has been touched, and has validation error
    return !!(this.validationErrors[fieldName] && this.fieldTouched[fieldName] && !this.validationDeferred);
  }

  getFieldErrorMessage(fieldName: string): string {
    return this.validationErrors[fieldName] || '';
  }

  hasFieldWarning(fieldName: string): boolean {
    return !!(this.validationState?.warnings && this.validationState.warnings[fieldName]);
  }

  getFieldWarningMessage(fieldName: string): string {
    return this.validationState?.warnings?.[fieldName] || '';
  }

  isValidationStateValid(): boolean {
    return this.validationState?.isValid || false;
  }

  /**
   * Clear phone number validation error when phone has exactly 10 digits
   * Similar to direct-quote-assist component implementation
   */
  clearCompanyPhoneError(): void {
    if (this.localQuoteDetails.companyPhone?.trim()) {
      // Get only the numeric digits from the formatted phone number
      const numericValue = this.localQuoteDetails.companyPhone.replace(/\D/g, '');
      if (numericValue.length === 10) {
        // Clear the validation error for this field
        this.validationErrors['companyPhone'] = '';
        // Only validate if validation is not deferred
        if (!this.validationDeferred) {
          this.validateField('companyPhone', this.localQuoteDetails.companyPhone);
        }
      }
    }
  }


  /**
   * Handle keypress events for city and state fields to only allow alphabetic input
   * @param event KeyboardEvent from the input field
   */
  onCityStateKeyPress(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, and navigation keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(event.keyCode) !== -1) {
      return;
    }
    
    // Allow only alphabetic characters (A-Z, a-z) and space
    const charCode = event.which ? event.which : event.keyCode;
    const char = String.fromCharCode(charCode);
    
    // Allow letters, spaces, special characters, and underscore (but not numbers)
    if (!/^[a-zA-Z\s\W_]$/.test(char)) {
      event.preventDefault();
      return;
    }
  }

} 
